import React, { useEffect, useState } from 'react';
import { Container, Typography, Box, Button, TextField, CircularProgress } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { api } from '../../../api';
import { localGet } from '../../../utils/localStorageUtils';

/**
 * DeAnonymizePanel — FEAT-022 / D4 de-anonymization workflow (portal side).
 *
 * A student report is anonymous to responders by default (the backend strips the `student` id).
 * Two-step, audited workflow to reveal a student's identity:
 *   1. A responder ESCALATES a request to de-anonymize (deAnonAction: "request") — this raises
 *      the incident and opens the request; it does NOT reveal identity.
 *   2. A DESIGNATED approver (Principal / Assistant Principal tier) approves (deAnonAction:
 *      "approve"). Only then is the identity unlocked, and the access is written to the
 *      immutable identity-access audit log (who / why / when / incident).
 *
 * Emergency incidents reveal identity immediately (Scenario A) — the student-details card is
 * already shown, so this panel doesn't render for them.
 */
const APPROVER_PERMISSIONS = ['schoolAdmin', 'districtAdmin', 'disclosureAdmin'];

const DeAnonymizePanel = ({ data }) => {
  const theme = useTheme();
  const [user, setUser] = useState(null);
  const [requested, setRequested] = useState(data?.deAnonRequested === 'True');
  const [approved, setApproved] = useState(data?.deAnonApproved === 'True');
  const [denied, setDenied] = useState(data?.deAnonDenied === 'True' && data?.deAnonRequested !== 'True');
  const [reason, setReason] = useState(data?.deAnonReason || '');
  const [reasonInput, setReasonInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const [auditLog, setAuditLog] = useState([]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const u = await localGet('user', true);
      if (!mounted) return;
      setUser(u);
      // Approver-tier users can review the identity-access history for this report.
      if (u && APPROVER_PERMISSIONS.includes(u.permission)) {
        const res = await api('get', { incidentId: data.id }, 'identityAccessLogs');
        if (mounted && res?.data?.data) setAuditLog(res.data.data);
      }
    })();
    return () => { mounted = false; };
  }, [data.id]);

  // Emergency reveals identity immediately (Scenario A); the details card already shows it.
  if (data?.emergency === 'True') return null;

  const isApprover = Boolean(user && APPROVER_PERMISSIONS.includes(user.permission));

  const requestDeAnon = async () => {
    setBusy(true); setError('');
    const res = await api('patch', { id: data.id, deAnonAction: 'request', deAnonReason: reasonInput }, 'incidents');
    if (res?.data?.success) {
      setRequested(true);
      setDenied(false);
      setReason(reasonInput);
    } else {
      setError(res?.data?.message || 'Could not submit the request. Please try again.');
    }
    setBusy(false);
  };

  const approveDeAnon = async () => {
    setBusy(true); setError('');
    const res = await api('patch', { id: data.id, deAnonAction: 'approve' }, 'incidents');
    if (res?.data?.success) {
      setApproved(true);
    } else {
      setError(res?.data?.message || 'Could not approve the de-anonymization.');
    }
    setBusy(false);
  };

  const denyDeAnon = async () => {
    setBusy(true); setError('');
    const res = await api('patch', { id: data.id, deAnonAction: 'deny' }, 'incidents');
    if (res?.data?.success) {
      setRequested(false);
      setDenied(true);
    } else {
      setError(res?.data?.message || 'Could not deny the request.');
    }
    setBusy(false);
  };

  const style = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      marginLeft: '60px',
      marginRight: '60px',
      marginTop: '40px',
      width: 'auto',
      paddingLeft: '0px !important',
      border: `1px solid ${theme.palette.custom?.gray || '#ccc'}`,
      borderRadius: '4px',
    },
    header: { borderBottom: `1px solid ${theme.palette.custom?.gray || '#ccc'}` },
    title: { fontSize: '20px', fontWeight: '700', marginTop: '20px', marginBottom: '20px', marginLeft: '20px' },
    body: { padding: '20px', display: 'flex', flexDirection: 'column', gap: '12px' },
    text: { fontSize: '14px', color: theme.palette.text?.secondary || '#666', lineHeight: 1.5 },
    note: { fontSize: '12px', color: theme.palette.text?.secondary || '#888', fontStyle: 'italic' },
    error: { fontSize: '13px', color: theme.palette.error?.main || '#d32f2f' },
    reasonBox: { fontSize: '14px', color: theme.palette.custom?.black || '#222', fontWeight: 600 },
    actions: { marginTop: '4px' },
    actionsRow: { marginTop: '4px', display: 'flex', flexDirection: 'row', gap: '12px', flexWrap: 'wrap' },
    // This theme drives colors off palette.custom.*; without an explicit input color the text
    // renders invisibly. Force a visible input + placeholder color.
    reasonField: {
      '& .MuiInputBase-input': { color: theme.palette.custom?.black || '#222' },
      '& .MuiInputBase-input::placeholder': { color: theme.palette.custom?.gray6 || '#5B5B5B', opacity: 1 },
    },
    auditWrap: {
      marginTop: '16px',
      paddingTop: '12px',
      borderTop: `1px solid ${theme.palette.custom?.gray || '#eee'}`,
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
    },
    auditTitle: { fontSize: '14px', fontWeight: 700, color: theme.palette.text?.primary || '#222' },
    auditRow: { display: 'flex', flexDirection: 'column' },
    auditPrimary: { fontSize: '13px', color: theme.palette.text?.primary || '#222', fontWeight: 600 },
    auditMeta: { fontSize: '12px', color: theme.palette.text?.secondary || '#666' },
  };

  // Identity-access history (approver-tier only) — shown under every state.
  const auditHistory = auditLog.length > 0 ? (
    <Box sx={style.auditWrap}>
      <Typography sx={style.auditTitle}>Identity-access history</Typography>
      {auditLog.map((entry) => (
        <Box key={entry.id} sx={style.auditRow}>
          <Typography sx={style.auditPrimary}>
            {entry.accessorName || 'Unknown'}{entry.accessorPermission ? ` (${entry.accessorPermission})` : ''}
          </Typography>
          <Typography sx={style.auditMeta}>
            {entry.accessedAt ? new Date(entry.accessedAt).toLocaleString() : ''}
            {entry.reason ? ` · ${entry.reason === 'approved-deanon' ? 'approved de-anonymization' : 'imminent emergency'}` : ''}
          </Typography>
        </Box>
      ))}
    </Box>
  ) : null;

  let content;
  if (approved) {
    // Approved (this session or already on the record).
    content = (
      <Typography sx={style.text}>
        This report has been de-anonymized. Reopen the report from the list to view the
        student's details. This access has been recorded in the identity-access audit log.
      </Typography>
    );
  } else if (requested) {
    // Requested, awaiting approval.
    content = (
      <>
        <Typography sx={style.text}>
          A request to reveal this student's identity has been submitted and the case escalated.
        </Typography>
        {reason ? <Typography sx={style.reasonBox}>Reason: {reason}</Typography> : null}
        {isApprover ? (
          <>
            <Typography sx={style.text}>
              As a designated approver, you can de-anonymize this report. This action is logged.
            </Typography>
            {error ? <Typography sx={style.error}>{error}</Typography> : null}
            <Box sx={style.actionsRow}>
              <Button variant="contained" color="warning" disabled={busy} onClick={approveDeAnon}>
                {busy ? <CircularProgress size={20} /> : 'Approve de-anonymization'}
              </Button>
              <Button variant="outlined" color="inherit" disabled={busy} onClick={denyDeAnon}>
                Deny request
              </Button>
            </Box>
          </>
        ) : (
          <Typography sx={style.note}>
            Awaiting approval from a designated approver (Principal / Assistant Principal).
          </Typography>
        )}
      </>
    );
  } else {
    // Anonymous, no request yet (or a prior request was denied).
    content = (
      <>
        {denied ? (
          <Typography sx={style.note}>
            A previous de-anonymization request was denied by a designated approver. You may submit
            a new request with additional justification if the situation warrants it.
          </Typography>
        ) : null}
        <Typography sx={style.text}>
          This report is anonymous. If safety requires identifying the student, you can request a
          de-anonymization. A designated approver must approve it, and the access is recorded in the
          identity-access audit log.
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={3}
          sx={style.reasonField}
          label="Reason for de-anonymization (required)"
          placeholder="e.g., Student reported repeated threats of physical harm from a named peer; identity needed to coordinate a safety plan with the counselor and notify guardians."
          helperText="Explain why identifying this student is necessary for their safety. This justification is recorded in the audit log."
          value={reasonInput}
          onChange={(e) => setReasonInput(e.target.value)}
        />
        {error ? <Typography sx={style.error}>{error}</Typography> : null}
        <Box sx={style.actions}>
          <Button
            variant="contained"
            color="warning"
            disabled={busy || !reasonInput.trim()}
            onClick={requestDeAnon}
          >
            {busy ? <CircularProgress size={20} /> : 'Request de-anonymization'}
          </Button>
        </Box>
      </>
    );
  }

  return (
    <Container sx={style.container}>
      <Container sx={style.header}>
        <Typography sx={style.title}>Student Identity</Typography>
      </Container>
      <Box sx={style.body}>
        {content}
        {auditHistory}
      </Box>
    </Container>
  );
};

export default DeAnonymizePanel;
